import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { PartInfo as PartInfoType } from '../types';

interface PartInfoProps {
    part: PartInfoType;
}

export const PartInfo: React.FC<PartInfoProps> = ({ part }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    const handleImageError = () => {
        setImageError(true);
        setImageLoaded(true);
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Part Image */}
                    {part.imageUrl && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ 
                                maxWidth: 400,
                                height: 300, // Fixed height to prevent layout shifts
                                mx: 'auto',
                                mb: 3,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                overflow: 'hidden'
                            }}>
                                {!imageLoaded && !imageError && (
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height="100%"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            borderRadius: 2
                                        }}
                                    />
                                )}

                                {!imageError && (
                                    <img 
                                        src={part.imageUrl} 
                                        alt={part.name}
                                        onLoad={handleImageLoad}
                                        onError={handleImageError}
                                        style={{ 
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            borderRadius: 8,
                                            opacity: imageLoaded ? 1 : 0,
                                            transition: 'opacity 0.3s ease-in-out'
                                        }}
                                    />
                                )}

                                {imageError && (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center', 
                                        color: 'text.secondary' 
                                    }}>
                                        <Typography variant="body2">
                                            Image unavailable
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Part Name */}
                    <Box>
                        <Typography variant="h4" component="div" gutterBottom align="center">
                            {part.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" gutterBottom align="center">
                            Similarity: {(part.similarity * 100.0).toFixed(2)}%
                        </Typography>
                    </Box>

                    {/* Description */}
                    <Box>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            Description
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {part.descriptionProdDescriptionLong}
                        </Typography>
                    </Box>

                    {/* Information Grid */}
                    <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 3
                    }}>
                        {/* Supplier Information */}
                        <Box>
                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                Supplier Information
                            </Typography>
                            <Typography variant="body1">
                                Code: {part.supplierCode}
                            </Typography>
                            <Typography variant="body1">
                                Name: {part.supplierName}
                            </Typography>
                        </Box>

                        {/* Product Information */}
                        <Box>
                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                Product Information
                            </Typography>
                            <Typography variant="body1">
                                Category: {part.productCategory}
                            </Typography>
                            <Typography variant="body1">
                                Subcategory: {part.productSubCategory}
                            </Typography>
                            <Typography variant="body1">
                                Level: {part.viparProductLevel}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};
